// Trade Journal — tab toggle between raw deals and grouped closed positions,
// with per-position notes/tags affordances. Presentational: the parent
// (account-detail) owns the data fetches and the note/tag drawer handlers and
// passes them in as props. Extracted verbatim from account-detail.tsx
// (2026-05-28 refactor — no behavioral change).

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cn, fmtLocalDateTime, fmtRMultiple } from '@/lib/utils';
import { Chip, Chips, Panel, PanelBody, PanelHead, SideBadge, SourceBadge } from '@/components/ui';
import { TagChipList, NoteButton, TagButton } from '@/components/journal';
import { sideOf, sourceOf, type TradeRow } from '@/lib/metrics';
import { rMultiple } from '@/lib/r-multiples';
import type { ClosedPosition, Tag, TradeNote } from '@/types/veritas';

// Sort-column keys for the deals table. Defined here so both the panel
// header sort indicators and the row sorter share one source of truth.
type SortKey = 'snapshot_ts' | 'deal_ticket' | 'symbol' | 'deal_type' | 'volume' | 'magic' | 'profit';

type JournalView = 'deals' | 'positions';

export function TradeJournal({
  trades,
  positions,
  dealCount,
  posCount,
  loadingMore,
  onLoadMore,
  notes,
  tags,
  positionTags,
  onOpenNote,
  onOpenTags,
  readOnly,
}: {
  trades: TradeRow[];
  positions: ClosedPosition[];
  // Option 2: `trades`/`positions` are the loaded window; *Count is the full
  // server-side total for the current magic+source scope. onLoadMore raises
  // the window. Filters/sort/search below operate on the loaded rows.
  dealCount: number;
  posCount: number;
  loadingMore: boolean;
  onLoadMore: () => void;
  notes: TradeNote[];
  tags: Tag[];
  positionTags: Map<number, Set<string>>;
  onOpenNote: (p: { account_login: number; position_ticket: number; credential_id: string; symbol: string; closed_at: string | null }) => void;
  onOpenTags: (p: { account_login: number; position_ticket: number; credential_id: string; symbol: string; closed_at: string | null }) => void;
  // When true (shared read-only viewer), hide per-trade note/tag editing.
  readOnly?: boolean;
}) {
  const [view, setView] = useState<JournalView>('positions');
  const notesByPos = useMemo(() => {
    const m = new Map<number, TradeNote>();
    for (const n of notes) m.set(n.position_ticket, n);
    return m;
  }, [notes]);
  const tagsById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  return (
    <Panel>
      <PanelHead
        title={
          <>
            Trade Journal{' '}
            <span className="text-text-dim">
              {view === 'positions'
                ? `${positions.length} of ${posCount} closed positions`
                : `${trades.length} of ${dealCount} deals`}
            </span>
          </>
        }
        right={
          <Chips>
            <Chip active={view === 'positions'} onClick={() => setView('positions')}>
              Positions
            </Chip>
            <Chip active={view === 'deals'} onClick={() => setView('deals')}>
              Deals
            </Chip>
          </Chips>
        }
      />
      <PanelBody flush>
        {view === 'deals' ? (
          <TradeTableInner rows={trades} />
        ) : (
          <PositionsTable
            rows={positions}
            notesByPos={notesByPos}
            positionTags={positionTags}
            tagsById={tagsById}
            onOpenNote={onOpenNote}
            onOpenTags={onOpenTags}
            readOnly={readOnly}
          />
        )}
        {(view === 'deals' ? trades.length < dealCount : positions.length < posCount) && (
          <div className="flex items-center justify-center border-t border-line-soft px-4 py-3">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="rounded-md border border-line bg-bg-2 px-4 py-1.5 text-[11.5px] font-semibold text-text-mid transition-colors hover:border-green hover:text-green disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loadingMore
                ? 'Loading…'
                : `Load more (${view === 'deals' ? trades.length : positions.length} of ${view === 'deals' ? dealCount : posCount})`}
            </button>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

// Inner deals table — same logic as the old TradeTable but stripped of its
// own panel chrome (the parent owns the Panel now).
function TradeTableInner({ rows }: { rows: TradeRow[] }) {
  const [search, setSearch] = useState('');
  const [side, setSide] = useState<'all' | 'BUY' | 'SELL'>('all');
  const [result, setResult] = useState<'all' | 'win' | 'loss'>('all');
  const [src, setSrc] = useState<'all' | 'bot' | 'manual'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('snapshot_ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Option 2: the running "Balance" column was dropped — it was cumulative from
  // the first deal, which is impossible once only a recent window is loaded.
  // Filters/sort/search operate on the loaded rows.
  const filtered = useMemo(() => {
    let xs = rows;
    if (side !== 'all')   xs = xs.filter((t) => sideOf(t) === side);
    if (result === 'win')  xs = xs.filter((t) => t.profit > 0);
    if (result === 'loss') xs = xs.filter((t) => t.profit < 0);
    if (src !== 'all')    xs = xs.filter((t) => sourceOf(t) === src);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter((t) =>
        String(t.deal_ticket).includes(q) ||
        t.snapshot_ts.toLowerCase().includes(q) ||
        t.symbol.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...xs].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortKey];
      const bv = (b as Record<string, unknown>)[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, side, result, src, search, sortKey, sortDir]);

  const headerCell = (key: SortKey, label: string) => {
    const active = sortKey === key;
    return (
      <th
        onClick={() => {
          if (active) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else { setSortKey(key); setSortDir('desc'); }
        }}
        className={cn(
          'sticky top-0 z-[1] cursor-pointer select-none border-b border-line bg-bg-2 px-3.5 py-2.5 text-left text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim',
          active ? 'text-green' : 'hover:text-green',
        )}
      >
        {label}
        <span className={cn('ml-1 text-[9px]', active ? 'opacity-100' : 'opacity-50')}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
        </span>
      </th>
    );
  };

  return (
    <>
      {/* Filter row. On phones the chip groups are wider than the panel,
          so we let them overflow horizontally with a one-line scroll-snap
          (search input stays full-width above the chip strip). On md+ they
          all sit inline. */}
      <div className="border-b border-line-soft px-[18px] py-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ticket / time / symbol…"
          className="mb-2 w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors focus:border-green md:mb-0 md:mr-2.5 md:w-[180px] md:py-[7px] md:text-[12px] md:inline-block"
        />
        <div className="thin-scroll -mx-[18px] flex items-center gap-2.5 overflow-x-auto px-[18px] pb-1 md:mx-0 md:inline-flex md:overflow-visible md:px-0 md:pb-0">
          <Chips>
            <Chip active={side === 'all'}  onClick={() => setSide('all')}>All</Chip>
            <Chip active={side === 'BUY'}  onClick={() => setSide('BUY')}>Buy</Chip>
            <Chip active={side === 'SELL'} onClick={() => setSide('SELL')}>Sell</Chip>
          </Chips>
          <Chips>
            <Chip active={result === 'all'}  onClick={() => setResult('all')}>All</Chip>
            <Chip active={result === 'win'}  onClick={() => setResult('win')}>Wins</Chip>
            <Chip active={result === 'loss'} onClick={() => setResult('loss')}>Losses</Chip>
          </Chips>
          <Chips>
            <Chip active={src === 'all'}    onClick={() => setSrc('all')}>All Sources</Chip>
            <Chip active={src === 'bot'}    alt onClick={() => setSrc('bot')}>Bot</Chip>
            <Chip active={src === 'manual'} onClick={() => setSrc('manual')}>Manual</Chip>
          </Chips>
        </div>
      </div>
      <div className="thin-scroll max-h-[520px] overflow-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr>
              {headerCell('snapshot_ts', 'Time')}
              {headerCell('deal_ticket', 'Ticket')}
              {headerCell('symbol', 'Symbol')}
              {headerCell('deal_type', 'Side')}
              {headerCell('volume', 'Vol')}
              {headerCell('magic', 'Source')}
              {headerCell('profit', 'P&L')}
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.deal_ticket} className="border-b border-line-soft hover:bg-white/[0.02]">
                <td className="num px-3.5 py-2.5 text-[11.5px] text-text-mid"
                    title={`${t.snapshot_ts} (UTC)`}>
                  {fmtLocalDateTime(t.snapshot_ts)}
                </td>
                <td className="num px-3.5 py-2.5 text-[11.5px] text-text-mid">{t.deal_ticket}</td>
                <td className="num px-3.5 py-2.5 text-[11.5px]">{t.symbol}</td>
                <td className="px-3.5 py-2.5"><SideBadge side={sideOf(t)} /></td>
                <td className="num px-3.5 py-2.5 text-[11.5px]">{t.volume.toFixed(2)}</td>
                <td className="px-3.5 py-2.5">
                  <SourceBadge kind={sourceOf(t)} />{' '}
                  {t.magic ? <span className="num text-[10px] text-text-dim">{t.magic}</span> : null}
                </td>
                <td className={cn('num px-3.5 py-2.5 text-[11.5px]', t.profit >= 0 ? 'text-green' : 'text-red')}>
                  {t.profit >= 0 ? '+' : ''}{t.profit.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════
// PositionsTable — one row per closed position. Pulls from the
// closed_positions view; doesn't need the same filters as Deals because
// position-level data is already aggregated.
// ════════════════════════════════════════════════════════════════════════
function PositionsTable({
  rows,
  notesByPos,
  positionTags,
  tagsById,
  onOpenNote,
  onOpenTags,
  readOnly,
}: {
  rows: ClosedPosition[];
  notesByPos: Map<number, TradeNote>;
  positionTags: Map<number, Set<string>>;
  tagsById: Map<string, Tag>;
  onOpenNote: (p: { account_login: number; position_ticket: number; credential_id: string; symbol: string; closed_at: string | null }) => void;
  onOpenTags: (p: { account_login: number; position_ticket: number; credential_id: string; symbol: string; closed_at: string | null }) => void;
  readOnly?: boolean;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) =>
      String(b.closed_at ?? '').localeCompare(String(a.closed_at ?? ''))),
    [rows],
  );

  const fmtDuration = (a: string | null, b: string | null): string => {
    if (!a || !b) return '—';
    const ms = Date.parse(b) - Date.parse(a);
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return m % 60 === 0 ? `${h}h` : `${h}h ${m % 60}m`;
  };

  return (
    <div className="thin-scroll max-h-[640px] overflow-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr>
            <Th>Closed</Th>
            <Th>Symbol</Th>
            <Th>Side</Th>
            <Th>Vol</Th>
            <Th>Hold</Th>
            <Th>R</Th>
            <Th>Net P&amp;L</Th>
            <Th>Tags</Th>
            <Th>Source</Th>
            <Th>{''}</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-12 text-center text-text-dim">
                No closed positions in this range.
              </td>
            </tr>
          )}
          {sorted.map((p) => {
            const side = p.side_code === 0 ? 'BUY' : p.side_code === 1 ? 'SELL' : null;
            const r = rMultiple(p).r;
            const note = notesByPos.get(p.position_ticket);
            const tagSet = positionTags.get(p.position_ticket) ?? new Set<string>();
            const target = {
              account_login: p.account_login,
              position_ticket: p.position_ticket,
              credential_id: p.credential_id,
              symbol: p.symbol,
              closed_at: p.closed_at,
            };
            return (
              <tr key={p.position_ticket} className="border-b border-line-soft hover:bg-white/[0.02]">
                <td className="num px-3.5 py-2.5 text-[11.5px] text-text-mid"
                    title={p.closed_at ? `${p.closed_at} (UTC)` : ''}>
                  {fmtLocalDateTime(p.closed_at)}
                </td>
                <td className="num px-3.5 py-2.5 text-[11.5px]">{p.symbol}</td>
                <td className="px-3.5 py-2.5">{side && <SideBadge side={side} />}</td>
                <td className="num px-3.5 py-2.5 text-[11.5px]">{p.opened_volume.toFixed(2)}</td>
                <td className="num px-3.5 py-2.5 text-[11.5px] text-text-mid">
                  {fmtDuration(p.opened_at, p.closed_at)}
                </td>
                <td
                  className={cn(
                    'num px-3.5 py-2.5 text-[11.5px] font-semibold',
                    r == null ? 'text-text-dim'
                    : r > 0 ? 'text-green'
                    : 'text-red',
                  )}
                  title={r == null ? 'No initial SL was set on this position' : undefined}
                >
                  {r == null ? '—' : fmtRMultiple(r)}
                </td>
                <td className={cn('num px-3.5 py-2.5 text-[11.5px]', p.realized_profit >= 0 ? 'text-green' : 'text-red')}>
                  {p.realized_profit >= 0 ? '+' : ''}{p.realized_profit.toFixed(2)}
                </td>
                <td className="px-3.5 py-2.5">
                  <TagChipList tagIds={tagSet} tagsById={tagsById} max={3} />
                </td>
                <td className="px-3.5 py-2.5">
                  <SourceBadge kind={p.magic ? 'bot' : 'manual'} />
                </td>
                <td className="px-3.5 py-2.5">
                  {!readOnly && (
                    <span className="inline-flex items-center gap-1.5">
                      <NoteButton hasNote={!!note?.note || !!note?.screenshot_path}
                                  onClick={() => onOpenNote(target)} />
                      <TagButton count={tagSet.size}
                                 onClick={() => onOpenTags(target)} />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return (
    <th className="sticky top-0 z-[1] whitespace-nowrap border-b border-line bg-bg-2 px-3.5 py-2.5 text-left text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </th>
  );
}
